import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  TextFormatType,
  $createTextNode,
  $createParagraphNode,
} from "lexical";
import { QuoteNode, $isQuoteNode } from "@lexical/rich-text";
import {
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
} from "@lexical/list";
import { $isLinkNode, $toggleLink } from "@lexical/link";
import { Button, Icon, Input } from "@ttc3k/trekker";
import {
  Bold,
  Italic,
  Link,
  List,
  Quote,
  Underline,
  Check,
  Trash,
  Xmark,
} from "iconoir-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, styled } from "styled-system/jsx";

export function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [showLinkPopover, setShowLinkPopover] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const { t } = useTranslation("core");

  const handleFormatText = (format: TextFormatType) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.formatText(format);
      }
    });
  };

  const handleToggleBulletList = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Check if we're already in a list by looking at the nodes
        const nodes = selection.getNodes();
        const isInList = nodes.some(node => {
          let currentNode = node;
          while (currentNode) {
            if ($isListNode(currentNode)) {
              return true;
            }
            const parent = currentNode.getParent();
            if (parent) {
              currentNode = parent;
            } else {
              break;
            }
          }
          return false;
        });

        if (isInList) {
          // We're in a list, so remove it
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          // We're not in a list, so insert one
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        }
      }
    });
  };

  const handleToggleBlockquote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const nodes = selection.getNodes();
        const hasQuote = nodes.some(node => {
          let currentNode = node;
          while (currentNode) {
            if ($isQuoteNode(currentNode)) {
              return true;
            }
            const parent = currentNode.getParent();
            if (parent) {
              currentNode = parent;
            } else {
              break;
            }
          }
          return false;
        });

        if (hasQuote) {
          // Remove blockquote by replacing with paragraph
          nodes.forEach(node => {
            // Find the quote node by traversing up the tree
            let currentNode = node;
            while (currentNode) {
              if ($isQuoteNode(currentNode)) {
                // Get all children of the quote node
                const children = currentNode.getChildren();
                const paragraphNode = $createParagraphNode();

                // Move all children to the paragraph node
                children.forEach(child => {
                  paragraphNode.append(child);
                });

                // Replace the quote node with the paragraph
                currentNode.replace(paragraphNode);

                // Update selection to the new paragraph node
                paragraphNode.select();
                break;
              }
              const parent = currentNode.getParent();
              if (parent) {
                currentNode = parent;
              } else {
                break;
              }
            }
          });
        } else {
          // Add blockquote by wrapping selected content
          const textContent = selection.getTextContent();
          if (textContent) {
            const quoteNode = new QuoteNode();
            const textNode = $createTextNode(textContent);
            quoteNode.append(textNode);
            selection.insertNodes([quoteNode]);
          }
        }
      }
    });
  };

  const handleLinkClick = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Check if selection is within a link node
        const anchorNode = selection.anchor.getNode();
        const focusNode = selection.focus.getNode();

        // Find link nodes by traversing up the tree
        let linkNode = null;

        // Check anchor node and its parents
        let currentNode = anchorNode;
        while (currentNode && !linkNode) {
          if ($isLinkNode(currentNode)) {
            linkNode = currentNode;
            break;
          }
          const parent = currentNode.getParent();
          if (parent) {
            currentNode = parent;
          } else {
            break;
          }
        }

        // Check focus node and its parents if no link found yet
        if (!linkNode) {
          currentNode = focusNode;
          while (currentNode && !linkNode) {
            if ($isLinkNode(currentNode)) {
              linkNode = currentNode;
              break;
            }
            const parent = currentNode.getParent();
            if (parent) {
              currentNode = parent;
            } else {
              break;
            }
          }
        }

        if (linkNode) {
          setLinkUrl(linkNode.getURL());
        } else {
          // Get the selected text to pre-fill the URL field
          const selectedText = selection.getTextContent();
          if (selectedText && selectedText.match(/^https?:\/\//)) {
            setLinkUrl(selectedText);
          }
        }
      }
    });
    setShowLinkPopover(true);
  };

  const handleLinkSubmit = () => {
    editor.update(() => {
      if (linkUrl.trim() === "") {
        $toggleLink("");
      } else {
        const url = linkUrl.startsWith("http") ? linkUrl : `https://${linkUrl}`;
        $toggleLink(url, { target: "_blank" });
      }
    });
    setShowLinkPopover(false);
    setLinkUrl("");
  };

  const handleLinkCancel = () => {
    setShowLinkPopover(false);
    setLinkUrl("");
  };

  const handleRemoveLink = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Find the link node
        const anchorNode = selection.anchor.getNode();
        const focusNode = selection.focus.getNode();

        let linkNode = null;

        // Check anchor node and its parents
        let currentNode = anchorNode;
        while (currentNode && !linkNode) {
          if ($isLinkNode(currentNode)) {
            linkNode = currentNode;
            break;
          }
          const parent = currentNode.getParent();
          if (parent) {
            currentNode = parent;
          } else {
            break;
          }
        }

        // Check focus node and its parents if no link found yet
        if (!linkNode) {
          currentNode = focusNode;
          while (currentNode && !linkNode) {
            if ($isLinkNode(currentNode)) {
              linkNode = currentNode;
              break;
            }
            const parent = currentNode.getParent();
            if (parent) {
              currentNode = parent;
            } else {
              break;
            }
          }
        }

        if (linkNode) {
          // Remove the link node and replace it with its text content
          const textContent = linkNode.getTextContent();
          const textNode = $createTextNode(textContent);

          // Replace the link node with the text node
          linkNode.replace(textNode);

          // Update selection to the new text node
          textNode.select();
        }
      }
    });
    setShowLinkPopover(false);
    setLinkUrl("");
  };

  return (
    <>
      <Flex
        position={"absolute"}
        top={"2px"}
        right={"2px"}
        width={"calc(100% - 4px)"}
        px={"300"}
        py={"250"}
        borderBottom={"1px solid"}
        borderBottomColor={"ink.300"}
        alignItems={"center"}
        gap={"150"}
        bg={"white"}
        borderTopRadius={"150"}
        zIndex={10}
      >
        <ToolbarButton onClick={() => handleFormatText("bold")} type={"button"}>
          <Icon Element={Bold} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => handleFormatText("italic")}
          type={"button"}
        >
          <Icon Element={Italic} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => handleFormatText("underline")}
          type={"button"}
        >
          <Icon Element={Underline} />
        </ToolbarButton>
        <ToolbarButton onClick={handleToggleBulletList} type={"button"}>
          <Icon Element={List} />
        </ToolbarButton>
        <ToolbarButton onClick={handleLinkClick} type={"button"}>
          <Icon Element={Link} />
        </ToolbarButton>
        <ToolbarButton onClick={handleToggleBlockquote} type={"button"}>
          <Icon Element={Quote} />
        </ToolbarButton>
      </Flex>

      {showLinkPopover && (
        <LinkPopover>
          <Flex direction="column" gap="100">
            <Flex gap="100" justifyContent="flex-start" alignItems="center">
              <Input
                placeholder={t("core:TEXT_EDITOR.LINK_POPOVER.PLACEHOLDER")}
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleLinkSubmit();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    handleLinkCancel();
                  }
                }}
                autoFocus
              />

              <Button
                visual={"destructive"}
                mode={"light"}
                size={"sm"}
                flexShrink={0}
                Icon={Trash}
                onClick={handleRemoveLink}
              />
              <Button
                mode={"light"}
                Icon={Xmark}
                size={"sm"}
                flexShrink={0}
                onClick={handleLinkCancel}
              />
              <Button
                visual={"success"}
                mode={"light"}
                size={"sm"}
                flexShrink={0}
                Icon={Check}
                onClick={handleLinkSubmit}
              />
            </Flex>
          </Flex>
        </LinkPopover>
      )}
    </>
  );
}

const ToolbarButton = styled("button", {
  base: {
    cursor: "pointer",
    transitionProperty: "color",
    transitionTimingFunction: "default",
    transitionDuration: "normal",
    color: "icon.dark",
    _hover: {
      color: "icon.mid",
    },
  },
});

const LinkPopover = styled("div", {
  base: {
    position: "absolute",
    top: "60px",
    right: "2px",
    bg: "white",
    shadow: "lg",
    borderRadius: "100",
    py: "150",
    px: "200",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    zIndex: 20,
  },
});
